import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import lodashIsEqual from 'lodash/fp/isEqual';
import url from 'url';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';

import {
  isListingPreLease,
  getListingManagedPhotoUrls,
  getListingUrlPath,
  isSelfShowingListing,
  isPreLeaseActive,
} from 'common/listingHelpers';
import { getListingContactPhone } from 'common/pageHelpers';
import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_IMAGE_PRE_LEASE_COMING_PHOTOS_URL,
} from 'common/listingConstants';
import {
  USER_ROLE_ANONYMOUS,
  USER_ROLE_ADMIN,
  USER_ROLE_AGENT,
  USER_ROLE_APARTMENT_LOCATOR,
} from 'common/userRoles';

import {
  TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING_PHOTOS,
} from 'common/tourConstants';

import {
  SR_API_ACTION_GET_LISTING_BY_ID,
  SR_API_ACTION_ADMIN_UPDATE_LISTING_PHOTOS,
} from 'api/actionTypes';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_CONTACT_BUTTON_CLICKED,
  ANALYTICS_EVENT_CALL_BUTTON_CLICKED,
  ANALYTICS_EVENT_TEXT_BUTTON_CLICKED,
} from 'client/analytics/eventList';
import {
  makeListingApplicationContext,
} from 'client/analytics/makeApplicationContext';

import Navbar from 'client/components/Navbar';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrNavbarToastButton from 'client/components/SrNavbarToastButton';
import SrRequestShowingButton from 'client/components/SrRequestShowingButton';
import SrAgentAddListingToTourButton from 'client/components/SrAgentAddListingToTourButton';
import SrFlex from 'client/components/SrFlex';
import SrIcon from 'client/components/SrIcon';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrLightbox from 'client/components/SrLightbox';
import NavbarButton from 'client/components/NavbarButton';
import SrContactModal from 'client/components/SrContactModal';
import SrListingSelfTouringSection from 'client/components/SrListingSelfTouringSection';

import postWithState from 'client/actions/postWithState';
import { SCREEN_ID_LISTING_PHOTOS } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import moment from 'moment';
import { numberWithCommas } from 'common/formatters';
import moveElementInArray from 'common/util/moveElementInArray';
import { isUserAgentMobile } from 'common/util/isUserAgentMobileApp';
import ListingActions from 'client/actions/Listing';
import ApartmentActions from 'client/actions/Apartment';
import UserActions from 'client/actions/User';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import FloorPlanActions from 'client/actions/FloorPlan';

import branch from 'client/core/branch';
import SrSimpleMessageModal, { SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP } from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { BUTTON_THEME_GREEN_ADOBE, BUTTON_THEME_RED_ADOBE } from 'client/components/ButtonDeprecated';

@withTransitionHook
@withScreenId(SCREEN_ID_LISTING_PHOTOS)
@withScrollToTop
@withApiState({
  listingApiState: {
    action: SR_API_ACTION_GET_LISTING_BY_ID,
  },
})
@branch({
  listing: ['listing'],
  bookmarksByListingId: ['bookmarksByListingId'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  tenantScreeningCriteriaByPartner: ['tenantScreeningCriteriaByPartner'],
  tenantScreeningCriteriaByListingId: ['tenantScreeningCriteriaByListingId'],
  currentUser: ['currentUser'],
  assignedApartmentLocator: ['assignedApartmentLocator'],
  userRoles: ['currentUser', 'roles'],
  userAgent: ['analytics', 'userAgent'],
  appUrl: ['env', 'APP_URL'],
  showUnitsByFloorPlanId: ['floorPlans', 'showUnitsByFloorPlanId'],
  apartmentsByBuildingId: ['apartments', 'byBuildingId'],
})
class ListingPhotos extends Component {
  static fetchData(match, inject) {
    const currentUserId = inject.state.get(['currentUser', 'id']);

    return ListingActions
      .getById(inject, parseInt(match.params.listingId, 10))
      .then((listing) => {
        if (!listing) {
          return;
        }
        const promisesToResolve = [];
        if (
          listing.dataSource === LISTING_DATA_SOURCE_SUNROOM
          && listing.listingManagementCompany
        ) {
          promisesToResolve.push(TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
            managementCompanyName: listing.listingManagementCompany,
          }));
        }

        if (listing.dataSource === LISTING_DATA_SOURCE_SUNROOM) {
          promisesToResolve.push(TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
            listingId: listing.id,
          }));
        }

        if (listing.apartmentBuildingId) {
          promisesToResolve.push(ApartmentActions.getByApartmentBuildingId(inject, {
            apartmentBuildingId: listing.apartmentBuildingId,
          }));
        }

        if (listing.floorPlanId) {
          promisesToResolve.push(FloorPlanActions.getShowUnit(inject, {
            floorPlanId: listing.floorPlanId,
          }));
        }

        if (currentUserId) {
          promisesToResolve.push(UserActions.getApartmentLocator(inject, currentUserId));
        }
        return Promise.all(promisesToResolve);
      });
  }

  static getPageMetadata = (state) => {
    const listingMeta = state.get('listing') || {};
    const propertyTypeMeta = listingMeta && listingMeta.mlsNumber ? 'home' : 'apartment';
    const managedPhotoUrls = listingMeta && getListingManagedPhotoUrls({ listing: listingMeta });
    return {
      pageTitle: listingMeta.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
        ? (
          'Photos | Sunroom Rentals'
        ) : (
          listingMeta.addressStreetDisplay + ', ' +
          listingMeta.addressCity + ', ' +
          listingMeta.addressStateCode + ' ' +
          listingMeta.addressZip + ' - Photos | Sunroom Rentals'
        ),
      section: 'Listing',
      // canonical: '/',
      keywords: (
        'rent, home, apartment, austin, texas, tour, ' +
        listingMeta.addressZip + ', ' + listingMeta.addressCity
      ),
      description: (
        'Sunroom has ' + managedPhotoUrls.length +
        ' photos of this $' + numberWithCommas(listingMeta.price) + ' ' +
        propertyTypeMeta + ' with ' + listingMeta.bedrooms + ' beds ' +
        listingMeta.bathrooms + ' baths, ' + listingMeta.squareFeet + ' sqft at ' +
        listingMeta.addressStreetDisplay + ' available for move in ' +
        moment(listingMeta.availableDate).format('MMMM Do, YYYY')
      ),
      image: managedPhotoUrls[0],
    };
  }

  static propTypes = {
    listing: PropTypes.object,
    bookmarksByListingId: PropTypes.object,
    tenantScreeningCriteriaByPartner: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    assignedApartmentLocator: PropTypes.object,
    showUnitsByFloorPlanId: PropTypes.object.isRequired,
    apartmentsByBuildingId: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      isLightboxOpen: false,
      lightBoxPhotoIndex: 0,
      editMode: false,
      isOrderEdited: false,
      isHideShowEdited: false,
      managedPhotoUrls: [],
      hiddenPhotoUrls: [],
      sunroomPhotoUrls: [],
      showConfirmEditionModal: false,
    };
  }

  componentDidMount() {
    this._initializePhotoUrlsEditState(this.props.listing);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.listing) {
      if ((!this.props.listing && nextProps.listing) || this.props.listing.id !== nextProps.listing.id) {
        this._initializePhotoUrlsEditState(nextProps.listing);
      }
    }
  }

  _initializePhotoUrlsEditState(listing) {
    const { userRoles } = this.props;
    if (!listing) {
      return null;
    }

    const isAdmin = userRoles.includes(USER_ROLE_ADMIN);

    const managedPhotoUrls = getListingManagedPhotoUrls({
      listing,
      includeHidden: isAdmin,
    });

    this.setState({
      managedPhotoUrls: managedPhotoUrls,
      hiddenPhotoUrls: listing.hiddenPhotoUrls,
      sunroomPhotoUrls: listing.sunroomPhotoUrls,
    });
  }

  _updatePhotos = (e, shouldNotifyPartner = false) => {
    e.preventDefault();
    const {
      dispatch,
      listing,
      match,
    } = this.props;

    const {
      sunroomPhotoUrls,
      hiddenPhotoUrls,
    } = this.state;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_LISTING_PHOTOS,
      payload: {
        listingId: listing.id,
        hiddenPhotoUrls,
        sunroomPhotoUrls,
        shouldNotifyPartner,
      },
    })
      .then(() => {
        return dispatch(ListingActions.getById, {
          listingId: parseInt(match.params.listingId, 10),
          reload: true,
        });
      })
      .then((listing) => {
        this._initializePhotoUrlsEditState(listing);

        this.setState({
          editMode: false,
          isOrderEdited: false,
          isHideShowEdited: false,
          showConfirmEditionModal: false,
        });
      });
  }

  _toggleEditMode = () => {
    const {
      editMode,
      isOrderEdited,
      isHideShowEdited,
    } = this.state;

    if (editMode && (isOrderEdited || isHideShowEdited)) {
      this.setState({
        showConfirmEditionModal: true,
      });
    }
    else {
      this.setState({
        editMode: !editMode,
        isOrderEdited: false,
        isHideShowEdited: false,
      });
    }
  }

  _toggleHideShowPicture = (e, url) => {
    e.preventDefault();
    const { hiddenPhotoUrls } = this.state;
    const { listing } = this.props;
    const urlIndex = hiddenPhotoUrls.indexOf(url);
    let newHiddenPhotoUrls;

    if (urlIndex === -1) {
      newHiddenPhotoUrls = [...hiddenPhotoUrls, url];
    }
    else {
      newHiddenPhotoUrls = hiddenPhotoUrls.filter((hiddenPhotoUrl) => hiddenPhotoUrl !== url);
    }

    const isHideShowEdited = newHiddenPhotoUrls.length !== listing.hiddenPhotoUrls.length
      || !lodashIsEqual(newHiddenPhotoUrls.sort(), listing.hiddenPhotoUrls.slice().sort());

    this.setState({
      hiddenPhotoUrls: newHiddenPhotoUrls,
      isHideShowEdited: isHideShowEdited,
    });
  }

  _movePhotoUrl = (e, url, direction) => {
    e.preventDefault();
    const { sunroomPhotoUrls } = this.state;
    const { listing } = this.props;
    const newOrderedPhotoUrls = moveElementInArray(sunroomPhotoUrls, url, direction);
    const isOrderEdited = !lodashIsEqual(newOrderedPhotoUrls, listing.sunroomPhotoUrls);

    this.setState({
      sunroomPhotoUrls: newOrderedPhotoUrls,
      isOrderEdited: isOrderEdited,
    });
  }

  _moveTo = (e, url, position) => {
    e.preventDefault();
    const { sunroomPhotoUrls } = this.state;
    const { listing } = this.props;
    const newOrderedPhotoUrls = sunroomPhotoUrls.slice();
    if (position === 'end') {
      newOrderedPhotoUrls.push(
        newOrderedPhotoUrls.splice(newOrderedPhotoUrls.indexOf(url), 1)[0]
      );
    }
    else if (position === 'start') {
      newOrderedPhotoUrls.unshift(newOrderedPhotoUrls.splice(newOrderedPhotoUrls.indexOf(url), 1)[0]);
    }
    const isOrderEdited = !lodashIsEqual(newOrderedPhotoUrls, listing.sunroomPhotoUrls);

    this.setState({
      sunroomPhotoUrls: newOrderedPhotoUrls,
      isOrderEdited: isOrderEdited,
    });
  }

  _onPhoneClicked = (e) => {
    const { listing } = this.props;
    e.preventDefault();
    analytics.track(ANALYTICS_EVENT_CONTACT_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    this.setState({
      showContactModal: true,
    });
  }

  _onCloseContactModal = () => {
    this.setState({
      showContactModal: false,
    });
  }

  _onClickMessage = () => {
    const { dispatch, listing } = this.props;
    analytics.track(ANALYTICS_EVENT_TEXT_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    dispatch(ListingActions.askAQuestionButtonClick, listing.id);
  }

  _onClickCall = () => {
    const { dispatch, listing } = this.props;
    analytics.track(ANALYTICS_EVENT_CALL_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    dispatch(ListingActions.callButtonClick, listing.id);
  }

  _onBookmarkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const {
      listing,
      userRoles,
      bookmarksByListingId,
      history,
      dispatch,
    } = this.props;

    if (!listing) {
      return;
    }

    const isBookmarked = bookmarksByListingId ? !!bookmarksByListingId[listing.id] : false;

    if (userRoles.includes(USER_ROLE_ANONYMOUS)) {
      history.push(`/login?bookmarkRequested=${listing.id}`);
    }
    else if (isBookmarked) {
      dispatch(UserActions.removeBookmark, { listingId: listing.id });
    }
    else {
      dispatch(UserActions.addBookmark, { listingId: listing.id });
    }
  }

  _openLightbox = (e, index) => {
    e.preventDefault();
    this.setState({
      isLightboxOpen: true,
      lightBoxPhotoIndex: index,
    });
  }

  _onCloseLightbox = () => {
    this.setState({
      isLightboxOpen: false,
    });
  }

  _gotoPrevious = () => {
    const currentLightBoxPhotoIndex = this.state.lightBoxPhotoIndex;
    this.setState({
      lightBoxPhotoIndex: currentLightBoxPhotoIndex - 1,
    });
  }

  _gotoNext = () => {
    const currentLightBoxPhotoIndex = this.state.lightBoxPhotoIndex;
    this.setState({
      lightBoxPhotoIndex: currentLightBoxPhotoIndex + 1,
    });
  }

  render() {
    const {
      listing,
      listingApiState,
      userRoles,
      userAgent,
      appUrl,
      match: {
        params: {
          listingId,
        },
      },
      location,
      bookmarksByListingId,
      dispatch,
      currentUser,
      assignedApartmentLocator,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      renterProfilesByUserId,
      history,
      showUnitsByFloorPlanId,
      apartmentsByBuildingId,
    } = this.props;

    const {
      editMode,
      isHideShowEdited,
      isOrderEdited,
      hiddenPhotoUrls,
      managedPhotoUrls,
      sunroomPhotoUrls,
      lightBoxPhotoIndex,
      showContactModal,
      showConfirmEditionModal,
    } = this.state;

    const displayPhotoUrls = editMode
      ? [...sunroomPhotoUrls]
      : [...managedPhotoUrls];

    const isAdmin = userRoles.includes(USER_ROLE_ADMIN);
    const isAgent = userRoles.includes(USER_ROLE_AGENT);
    const isApartmentLocator = userRoles.includes(USER_ROLE_APARTMENT_LOCATOR);

    const isMobileViewport = isUserAgentMobile(userAgent);
    const isBookmarked = listing && bookmarksByListingId && !!bookmarksByListingId[listing.id];

    const sunroomListingLink = listing && listing && url.resolve(appUrl, getListingUrlPath(listing));
    const apartment = listing && apartmentsByBuildingId[listing.apartmentBuildingId] || null;

    const locationQuery = queryString.parse(location.search);
    const requestedFrom = locationQuery.requestedFrom
      ? locationQuery.requestedFrom
      : TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING_PHOTOS;

    const listingContactPhone = getListingContactPhone({
      currentUser,
      renterProfilesByUserId,
      listing,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      assignedApartmentLocator,
    });

    const showUnit = listing
      && listing.floorPlanId
      && showUnitsByFloorPlanId[listing.floorPlanId]
      || null;

    if (listing && isListingPreLease(listing) && isPreLeaseActive({ listing })) {
      displayPhotoUrls.push(LISTING_IMAGE_PRE_LEASE_COMING_PHOTOS_URL);
    }

    if (listing?.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT) {
      return <Redirect to={`/listing/${listingId}`} />;
    }

    const navbarOptions = {
      leftButton: (
        <SrLink
          className='back link'
          to={`/listing/${listingId}`}
          redirectToLastLocation
        >
          Back
        </SrLink>
      ),
      rightButton: (
        <SrNavbarToastButton
          toastLink={sunroomListingLink}
          toastContent='share link copied!'
          buttonContent={(
            <img
              className='share listing'
              alt='share listing'
              src='/images/share-icon.svg'
            />
          )}
          toggleToast
        />
      ),
      secondaryRightButton: listingContactPhone && (
        <NavbarButton
          onClick={this._onPhoneClicked}
        >
          <img
            className='phone listing'
            alt='phone listing'
            src='/images/phone-icon.svg'
          />
        </NavbarButton>
      ),
    };

    if (isAgent) {
      navbarOptions.redirectLogoTo = '/agent/tours';
    }

    return (
      <div className='page-listing'>
        {isAdmin && !isMobileViewport ? (
          <Navbar
            leftButton={
              <SrLink
                className='back link'
                to={`/listing/${listingId}`}
                redirectToLastLocation
              >
                Back
              </SrLink>
            }
            rightButton={isAdmin && !isMobileViewport ? (
              <div
                onClick={this._toggleEditMode}
                className='toggleEditMode'
              >
                {editMode ? (
                  <span>
                    {(isHideShowEdited || isOrderEdited) && 'Save and '} Exit
                  </span>
                ) : (
                  <span>
                    Edit Photos
                  </span>
                )}
              </div>
            ) : (
              <SrNavbarToastButton
                toastLink={sunroomListingLink}
                toastContent='link copied!'
                buttonContent='Share'
                toggleToast
              />
            )}
          />
        ) : (
          <Navbar {...navbarOptions} />
        )}
        <SrLightbox
          photoUrls={displayPhotoUrls}
          currentImage={lightBoxPhotoIndex}
          isOpen={this.state.isLightboxOpen}
          onClickPrev={this._gotoPrevious}
          onClickNext={this._gotoNext}
          onClose={this._onCloseLightbox}
          showImageCount={false}
          backdropClosesModal
        />
        <div>
          {listingApiState.inProgress && <ActivityIndicator /> }
          {!listingApiState.inProgress && listing &&
            <div
              style={{

              }}
              className={classnames(
                'photosContainer',
                'clearfix',
                editMode && 'editMode',
              )}
            >
              {!editMode && (
                <div
                  onClick={this._onBookmarkClick}
                  className={classnames(
                    'listingBookmarkIcon',
                    isBookmarked && 'active',
                  )}
                />
              )}
              {displayPhotoUrls.map((photoUrl, index) => (
                <div
                  key={photoUrl}
                  onClick={(e) => !editMode && !isMobileViewport && this._openLightbox(e, index)}
                  className={classnames(
                    'listingPhoto',
                    !editMode && hiddenPhotoUrls.includes(photoUrl) && 'hidden',
                  )}
                >
                  <img
                    src={photoUrl}
                    alt={index}
                    className={classnames(
                      hiddenPhotoUrls.includes(photoUrl) && 'hidden',
                    )}
                  />
                  <br />
                  {editMode && (
                    <div className='photoActionContainer'>
                      <span
                        onClick={(e) => this._toggleHideShowPicture(e, photoUrl)}
                        className='button white small-admin'
                      >
                        {hiddenPhotoUrls.includes(photoUrl) ? 'show' : 'hide'}
                      </span>
                    </div>
                  )}
                  {editMode && (
                    <div className='move'>
                      <span
                        className='arrow-up'
                        onClick={(e) => this._movePhotoUrl(e, photoUrl, -1)}
                      />
                      <span
                        className='arrow-down'
                        onClick={(e) => this._movePhotoUrl(e, photoUrl, 1)}
                      />
                    </div>
                  )}
                  {editMode && (
                    <div className='moveTo'>
                      <span
                        className='moveToButton'
                        onClick={(e) => this._moveTo(e, photoUrl, 'start')}
                      >
                        <span
                          role='img'
                          aria-label='moveToStart'
                        >
                          ⏫
                        </span>
                      </span>
                      <span
                        className='moveToButton'
                        onClick={(e) => this._moveTo(e, photoUrl, 'end')}
                      >
                        <span
                          role='img'
                          aria-label='moveToEnd'
                        >
                          ⏬
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {apartment && apartment.communityPhotoUrls.map((photoUrl, index) => (
                <div key={photoUrl} className='listingPhoto'>
                  <img
                    src={photoUrl}
                    alt={index}
                  />
                  <div className='apartmentLabel'>
                    <SrLink to={`/apartment/${apartment.id}`}>
                      <SrFlex align='center'>
                        <SrIcon className='apartment-filled-blue-small' />
                        <SrText
                          color='blue300'
                          weight='medium'
                          paddingLeft={6}
                        >
                          {apartment.name} →
                        </SrText>
                      </SrFlex>
                    </SrLink>
                  </div>
                </div>
              ))}
              {!isAgent
                && !isApartmentLocator
                && listing.dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT
                && !editMode
                && !listing.cantTourFlag
                && (
                  isSelfShowingListing(listing) ? (
                    <SrListingSelfTouringSection
                      redirectToApartmentFlow={apartment && apartment.selfTourFlowEnabled}
                      listing={listing}
                      dispatch={dispatch}
                      currentUser={currentUser}
                      history={history}
                      hideInstructions
                    />
                  ) : (
                    <SrRequestShowingButton
                      requestedFrom={requestedFrom}
                      listing={showUnit || listing}
                    />
                  )
                )}
              {isAgent && !editMode && (
                <SrAgentAddListingToTourButton />
              )}
            </div>
          }
        </div>
        {showContactModal &&
          <SrContactModal
            isOpen={showContactModal}
            onClose={this._onCloseContactModal}
            onClickMessage={this._onClickMessage}
            onClickCall={this._onClickCall}
            url={url.resolve(appUrl, getListingUrlPath(listing))}
            phoneNumber={listingContactPhone}
          />
        }
        {showConfirmEditionModal && (
          <SrSimpleMessageModal
            headline='Are you done uploading and sorting photos?'
            message='If so, we will notify the partner.'
            multiButtons={[
              {
                buttonText: 'Yes',
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                onButtonClick: (e) => this._updatePhotos(e, true),
              },
              {
                buttonText: 'No',
                buttonTheme: BUTTON_THEME_RED_ADOBE,
                onButtonClick: this._updatePhotos,
              },
            ]}
            maxWidth='375'
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </div>
    );
  }
}

ListingPhotos.displayName = 'ListingPhotos';

export default ListingPhotos;
